/**
 * Deterministic player movement with static collision, used identically by
 * the server (authoritative) and the client (prediction, DESIGN 5.6).
 *
 * Model: velocity is instantaneous (Magicka feel, nothing to tune away on
 * the client). The incoming `vel` is not read; it stays in the signature so
 * an inertia model could be added without touching callers. Knockback is
 * NOT applied here: the server integrates `Body.knock` separately.
 *
 * Determinism: only +, -, *, / and Math.sqrt, which are correctly rounded
 * on every engine, so the client replays the server bit for bit. No
 * Math.hypot (not correctly rounded, differs between engines) and no trig.
 */

import type { Rect, Vec2 } from './math.js';
import type { MoveDir, MoveInput, MoveTarget } from './protocol.js';
import type { Arena, Collider } from './arena.js';
import { resolveStaticCollision } from './arena.js';
import { CLIENT, PLAYER_RADIUS } from './constants.js';

export interface MoveResult { pos: Vec2; vel: Vec2 }

/** Bodies this close to a collider's surface count as touching it (steering). */
const TOUCH_EPS = 1e-6;
/** Below this squared length a tangent is "head-on" and gets the tie-break. */
const TANGENT_EPS2 = 1e-12;
const ZERO: Vec2 = { x: 0, y: 0 };
/**
 * The longest stride taken at once (u). A longer step (a late frame: an input's dt may be 0.25 s, 1.5 u at walking
 * pace) is walked in equal pieces, so it can't land past the middle of a 2 u door or a chasm and be pushed out on
 * the far side. A tick or a frame at walking or hasted pace stays one piece, so those floats are unchanged.
 */
export const MAX_STRIDE = 0.5;
/** At most this many pieces, whatever the step (the server caps dt; this guards the loop). */
export const MAX_PIECES = 16;

/**
 * Advance one body by dt seconds. `speed` is the final u/s after status and
 * channel multipliers (PLAYER_SPEED x sm). Target moves stop within
 * CLIENT.rmbStopDistance of the target; direction moves use the normalised
 * vector as-is; stop zeroes velocity. The result is pushed out of pillars,
 * the level's wall blocks and `extraCircles` (barrier/wall spell push-out is
 * the server's job) and clamped
 * inside the arena. Pure: never mutates its arguments, and the same inputs
 * always yield bit-identical floats.
 *
 * `extraCircles` defaults to [] so both spellings in DESIGN compile: the
 * 6-argument form of 2.10 (client prediction) and the 7-argument form of 7.3.
 * `radius` defaults to PLAYER_RADIUS; enemies may pass their own.
 *
 * The returned `vel` is the displacement actually made this step divided
 * by dt (so it is zero against a wall and at the target), which is what
 * remote clients extrapolate with (DESIGN 5.6).
 */
export function stepMovement(
  pos: Vec2,
  vel: Vec2,
  mv: MoveInput,
  speed: number,
  dt: number,
  arena: Arena,
  extraCircles: readonly Collider[] = [],
  radius: number = PLAYER_RADIUS,
): MoveResult {
  void vel;
  // NaN compares false, so a garbage dt or speed means "do not move".
  const step = dt > 0 && speed > 0 && Number.isFinite(dt) && Number.isFinite(speed) ? speed * dt : 0;
  const pieces = step > MAX_STRIDE ? Math.min(MAX_PIECES, Math.ceil(step / MAX_STRIDE)) : 1;
  const stride = step / pieces;
  let next = pos;
  for (let i = 0; i < pieces; i++) {
    const d = stride > 0 ? displacement(next, mv, stride, radius, arena, extraCircles) : ZERO;
    next = resolveStaticCollision({ x: next.x + d.x, y: next.y + d.y }, radius, arena, extraCircles);
  }
  const velOut: Vec2 = dt > 0 ? { x: (next.x - pos.x) / dt, y: (next.y - pos.y) / dt } : { x: 0, y: 0 };
  return { pos: next, vel: velOut };
}

/** Intended displacement before collision, by input kind. */
function displacement(
  pos: Vec2,
  mv: MoveInput,
  step: number,
  radius: number,
  arena: Arena,
  extraCircles: readonly Collider[],
): Vec2 {
  switch (mv.k) {
    case 's': return ZERO;
    case 'd': return dirDisplacement(mv, step);
    case 't': return targetDisplacement(pos, mv, step, radius, arena, extraCircles);
  }
}

/**
 * Arrow keys: the client sends unit vectors (8 directions, see moveDir8).
 * Anything longer than 1 is capped so a diagonal can never outrun a straight
 * walk; shorter vectors are taken as-is (DESIGN 7.3).
 */
function dirDisplacement(mv: MoveDir, step: number): Vec2 {
  const l2 = mv.x * mv.x + mv.y * mv.y;
  if (!(l2 > 0) || !Number.isFinite(l2)) return ZERO;
  const k = l2 > 1 ? step / Math.sqrt(l2) : step;
  return { x: mv.x * k, y: mv.y * k };
}

/**
 * RMB walk. The target itself is first pushed out of pillars and inside the
 * walls, so clicking a pillar walks to its edge and stops instead of
 * orbiting it forever. The body never overshoots the point.
 */
function targetDisplacement(
  pos: Vec2,
  mv: MoveTarget,
  step: number,
  radius: number,
  arena: Arena,
  extraCircles: readonly Collider[],
): Vec2 {
  if (!Number.isFinite(mv.x) || !Number.isFinite(mv.y)) return ZERO;
  const goal = resolveStaticCollision({ x: mv.x, y: mv.y }, radius, arena, extraCircles);
  const tx = goal.x - pos.x, ty = goal.y - pos.y;
  const d2 = tx * tx + ty * ty;
  const stop = CLIENT.rmbStopDistance;
  if (!(d2 > stop * stop)) return ZERO; // arrived
  const d = Math.sqrt(d2);
  const s = step < d ? step : d;
  const dir: Vec2 = { x: tx / d, y: ty / d };
  return steerAround(pos, dir, s, radius, arena, extraCircles);
}

/**
 * Click-to-move steering: a body touching a circle whose interior the
 * straight line enters walks around the circle at full speed instead of
 * grinding into it (the push-out alone only keeps the tangential component,
 * which is nearly nothing head-on). Arrow-key movement deliberately gets no
 * steering: the player is steering. Only the first touching circle steers;
 * the push-out pass resolves anything else.
 */
function steerAround(
  pos: Vec2,
  dir: Vec2,
  s: number,
  radius: number,
  arena: Arena,
  extraCircles: readonly Collider[],
): Vec2 {
  for (const p of arena.pillars) {
    const hug = hugCircle(pos, dir, s, p.x, p.y, p.r + radius);
    if (hug) return hug;
  }
  for (const b of arena.blocks) {
    const hug = hugRect(pos, dir, s, b, radius);
    if (hug) return hug;
  }
  for (const b of arena.deep) {
    const hug = hugRect(pos, dir, s, b, radius);
    if (hug) return hug;
  }
  for (const c of extraCircles) {
    const hug = hugCircle(pos, dir, s, c.pos.x, c.pos.y, c.r + radius);
    if (hug) return hug;
  }
  return { x: dir.x * s, y: dir.y * s };
}

/**
 * If `pos` touches the circle (cx, cy, minD) and `dir` points into it, the
 * displacement that moves `s` along the tangent and lands back on the
 * circle (an arc step without trig). Head-on ties go clockwise on screen.
 * Null when this circle does not constrain the move.
 */
function hugCircle(pos: Vec2, dir: Vec2, s: number, cx: number, cy: number, minD: number): Vec2 | null {
  const ox = pos.x - cx, oy = pos.y - cy;
  const o2 = ox * ox + oy * oy;
  const touch = minD + TOUCH_EPS;
  if (o2 > touch * touch || o2 === 0) return null;
  const od = Math.sqrt(o2);
  const nx = ox / od, ny = oy / od; // outward normal
  const into = dir.x * nx + dir.y * ny;
  if (into >= 0) return null; // moving away or along the surface
  let tx = dir.x - nx * into, ty = dir.y - ny * into;
  const t2 = tx * tx + ty * ty;
  if (t2 < TANGENT_EPS2) {
    tx = -ny; ty = nx;
  } else {
    const tl = Math.sqrt(t2);
    tx /= tl; ty /= tl;
  }
  // Slide along the tangent, then project back onto the circle's surface.
  const px = ox + tx * s, py = oy + ty * s;
  const pd = Math.sqrt(px * px + py * py);
  const k = minD / pd;
  return { x: cx + px * k - pos.x, y: cy + py * k - pos.y };
}

/**
 * The wall-box version of hugCircle: a body touching a face it walks into
 * slides along that face at full speed (head-on ties go clockwise on
 * screen); at a corner it rounds the corner like a circle of its own radius.
 * Null when this box does not constrain the move.
 */
function hugRect(pos: Vec2, dir: Vec2, s: number, b: Rect, radius: number): Vec2 | null {
  const qx = pos.x < b.x ? b.x : pos.x > b.x + b.w ? b.x + b.w : pos.x;
  const qy = pos.y < b.y ? b.y : pos.y > b.y + b.h ? b.y + b.h : pos.y;
  const ox = pos.x - qx, oy = pos.y - qy;
  const o2 = ox * ox + oy * oy;
  const touch = radius + TOUCH_EPS;
  if (o2 > touch * touch || o2 === 0) return null;
  if (ox !== 0 && oy !== 0) return hugCircle(pos, dir, s, qx, qy, radius);
  const nx = ox > 0 ? 1 : ox < 0 ? -1 : 0, ny = oy > 0 ? 1 : oy < 0 ? -1 : 0; // outward face normal
  const into = dir.x * nx + dir.y * ny;
  if (into >= 0) return null;
  let tx = dir.x - nx * into, ty = dir.y - ny * into;
  const t2 = tx * tx + ty * ty;
  if (t2 < TANGENT_EPS2) {
    tx = -ny; ty = nx;
  } else {
    const tl = Math.sqrt(t2);
    tx /= tl; ty /= tl;
  }
  return { x: tx * s, y: ty * s };
}

/**
 * The 8-direction MoveDir the arrow keys produce (DESIGN 6.1), built from
 * the sign of each axis so client and server agree on the exact floats:
 * diagonals are (+-SQRT1_2, +-SQRT1_2), which isMoveInput accepts. No keys
 * held is a stop.
 */
export function moveDir8(dx: number, dy: number): MoveInput {
  const sx = dx > 0 ? 1 : dx < 0 ? -1 : 0;
  const sy = dy > 0 ? 1 : dy < 0 ? -1 : 0;
  if (sx === 0 && sy === 0) return { k: 's' };
  const k = sx !== 0 && sy !== 0 ? Math.SQRT1_2 : 1;
  return { k: 'd', x: sx * k, y: sy * k };
}
